<template>
    <div class="finance_box" ref="myElementRef">
        <div ref="searchBox">
            <hc-search :data="searchData" @serch="getSearch" />
        </div>

        <el-alert
            v-if="!isSuperAdmin"
            :title="t('attendance.captainReviewHint')"
            type="warning"
            :closable="false"
            class="role-alert"
        />

        <div class="table_box">
            <hc-table :loading="state.loading"
                :tableData="state.tableData"
                :column="state.tableHeader"
                :offsetHeight="state.offsetHeight"
                :maxPage="maxPage"
                @pageChange="pageChange"
                @limitChange="limitChange"
            >
                <template #operation="{ row }">
                    <el-button
                        v-if="isSuperAdmin"
                        class="opBtn"
                        size="small"
                        type="primary"
                        link
                        @click="openReviewDialog(row)"
                    >
                        {{ t('attendance.clockReview') }}
                    </el-button>
                </template>
            </hc-table>
        </div>

        <el-dialog
            v-model="reviewDialogVisible"
            :title="reviewDialogTitle"
            width="980px"
            destroy-on-close
        >
            <div class="dialog-toolbar">
                <el-radio-group v-model="reviewTab" @change="onChangeReviewTab">
                    <el-radio-button :label="1">{{ t('attendance.recentTwoDays') }}</el-radio-button>
                    <el-radio-button :label="2">{{ t('attendance.history') }}</el-radio-button>
                </el-radio-group>
            </div>

            <div v-loading="reviewLoading" class="day-list-wrap">
                <el-empty v-if="dayList.length === 0" :description="t('attendance.noClockRecords')" />
                <div v-for="day in dayList" :key="day.checkin_time" class="day-card">
                    <div class="day-head">
                        <div class="day-title">{{ day.checkin_time }}</div>
                        <el-tag :type="hasEvaluated(day) ? 'success' : 'warning'" size="small">
                            {{ hasEvaluated(day) ? t('attendance.evaluated') : t('attendance.pendingEvaluate') }}
                        </el-tag>
                    </div>

                    <div class="line-item">
                        <span class="k">{{ t('attendance.onMachineColon') }}</span>
                        <span class="v">{{ renderCheckinLine(day.gotowork, 'checkin_time') }}</span>
                    </div>
                    <div v-if="hasRecords(day.gotowork)" class="photo-row">
                        <el-image
                            v-for="(url, index) in collectCheckinPhotos(day.gotowork)"
                            :key="`gotowork-${index}`"
                            :src="url"
                            class="record-img"
                            fit="cover"
                            :preview-src-list="collectCheckinPhotos(day.gotowork)"
                            :initial-index="index"
                            preview-teleported
                        />
                    </div>
                    <div class="line-item">
                        <span class="k">{{ t('attendance.offMachineColon') }}</span>
                        <span class="v">{{ renderCheckinLine(day.gooffwork, 'checkin_time') }}</span>
                    </div>
                    <div v-if="hasRecords(day.gooffwork)" class="photo-row">
                        <el-image
                            v-for="(url, index) in collectCheckinPhotos(day.gooffwork)"
                            :key="`gooffwork-${index}`"
                            :src="url"
                            class="record-img"
                            fit="cover"
                            :preview-src-list="collectCheckinPhotos(day.gooffwork)"
                            :initial-index="index"
                            preview-teleported
                        />
                    </div>
                    <div class="line-item">
                        <span class="k">{{ t('attendance.otColon') }}</span>
                        <span class="v">{{ renderCheckinLine(day.OT, 'contents') }}</span>
                    </div>
                    <div v-if="hasRecords(day.OT)" class="photo-row">
                        <el-image
                            v-for="(url, index) in collectCheckinPhotos(day.OT)"
                            :key="`ot-${index}`"
                            :src="url"
                            class="record-img"
                            fit="cover"
                            :preview-src-list="collectCheckinPhotos(day.OT)"
                            :initial-index="index"
                            preview-teleported
                        />
                    </div>
                    <div class="line-item">
                        <span class="k">{{ t('attendance.zhongzhiColon') }}</span>
                        <span class="v">{{ renderCheckinLine(day.zhongzhi, 'contents') }}</span>
                    </div>
                    <div v-if="hasRecords(day.zhongzhi)" class="photo-row">
                        <el-image
                            v-for="(url, index) in collectCheckinPhotos(day.zhongzhi)"
                            :key="`zhongzhi-${index}`"
                            :src="url"
                            class="record-img"
                            fit="cover"
                            :preview-src-list="collectCheckinPhotos(day.zhongzhi)"
                            :initial-index="index"
                            preview-teleported
                        />
                    </div>

                    <div v-if="hasEvaluated(day)" class="line-item">
                        <span class="k">{{ t('attendance.evaluateColon') }}</span>
                        <span class="v">{{ renderReviewSummary(day) }}</span>
                    </div>

                    <div class="card-actions">
                        <el-button
                            v-if="!hasEvaluated(day) && isSuperAdmin"
                            type="primary"
                            link
                            @click="openEvaluateDialog(day)"
                        >
                            {{ t('attendance.reviewAndEvaluate') }}
                        </el-button>
                    </div>
                </div>
            </div>
        </el-dialog>

        <el-dialog
            v-model="evaluateDialogVisible"
            :title="`${t('attendance.audit')}（${evaluateDate || '-'}）`"
            width="680px"
            destroy-on-close
        >
            <div class="popup-date-hint">{{ t('attendance.captainEvaluate') }} · {{ evaluateDate || "-" }}</div>
            <el-form label-width="120px">
                <el-form-item :label="t('attendance.evaluateContent')">
                    <el-input
                        v-model="evaluateForm.content"
                        type="textarea"
                        :rows="4"
                        maxlength="300"
                        show-word-limit
                        :placeholder="t('attendance.evaluateContentPlaceholder')"
                    />
                </el-form-item>

                <el-form-item v-if="showEfficiencySwitch" :label="t('attendance.onMachine')">
                    <div class="eval-switch-row">
                        <span>{{ t('attendance.reject') }}</span>
                        <el-switch v-model="evaluateForm.working_efficiency" />
                        <span>{{ t('attendance.pass') }}</span>
                    </div>
                </el-form-item>

                <el-form-item v-if="showAttitudeSwitch" :label="t('attendance.offMachine')">
                    <div class="eval-switch-row">
                        <span>{{ t('attendance.reject') }}</span>
                        <el-switch v-model="evaluateForm.working_attitude" />
                        <span>{{ t('attendance.pass') }}</span>
                    </div>
                </el-form-item>

                <el-form-item v-if="showConstructionFeedbackSwitch" :label="t('attendance.otZhongzhiClock')">
                    <div class="eval-switch-row">
                        <span>{{ t('attendance.reject') }}</span>
                        <el-switch v-model="evaluateForm.construction_feedback" />
                        <span>{{ t('attendance.pass') }}</span>
                    </div>
                </el-form-item>

                <el-form-item :label="t('attendance.complaint')">
                    <div class="eval-switch-row">
                        <span>{{ t('common.no') }}</span>
                        <el-switch v-model="evaluateForm.complaint_deduction" />
                        <span>{{ t('common.yes') }}</span>
                    </div>
                </el-form-item>
            </el-form>

            <template #footer>
                <div class="dialog-footer">
                    <el-button @click="evaluateDialogVisible = false">{{ t('common.cancel') }}</el-button>
                    <el-button type="primary" :loading="submitting" @click="submitEvaluate">{{ t('attendance.submitReview') }}</el-button>
                </div>
            </template>
        </el-dialog>
    </div>
</template>

<script setup>
import hcSearch from "@/components/hc-search/index.vue";
import hcTable from "@/components/hc-table/index.vue";
import { reactive, ref, nextTick, onMounted, onUnmounted, toRaw, computed } from "vue";
import { ElMessage } from "element-plus";
import { listToMap } from "@/utils/utils";
import {
    captainListApi,
    captainCheckinDaysApi,
    saveCaptainReviewApi
} from "@/api/attendance/captainReview";
import { isTenantAdminRole } from "@/utils/adminMenuPermission.js";
import { signOssUrls } from "@/utils/ossImage.js";
import { useAttendancePageI18n, t } from '@/composables/useAttendancePageI18n.js';

const myElementRef = ref();
const searchBox = ref();

const page = ref(1);
const limit = ref(20);
const maxPage = ref(0);

const reviewDialogVisible = ref(false);
const reviewDialogTitle = ref("");
const reviewTab = ref(1);
const reviewLoading = ref(false);
const dayList = ref([]);
const selectedCaptain = ref(null);

const evaluateDialogVisible = ref(false);
const evaluateDate = ref("");
const selectedDay = ref(null);
const submitting = ref(false);
const evaluateForm = reactive({
    content: "",
    working_efficiency: false,
    working_attitude: false,
    construction_feedback: false,
    complaint_deduction: false
});

const userInfoRaw = JSON.parse(localStorage.getItem("user") || "{}");
const currentUser = userInfoRaw?.user?.users || userInfoRaw?.users || {};
const roleId = Number(currentUser?.roleId || currentUser?.role_id || 0);
const reviewerId = Number(currentUser?.id || 0);
const isSuperAdmin = computed(() => isTenantAdminRole(roleId));

const searchData = reactive([
    {
        label: "",
        type: "input",
        value: "",
        placeholder: "",
        field: "keywords",
    },
]);

const buildColumns = () => ([
    { show: true, type: "index", label: t('table.index'), width: 80 },
    { prop: "real_name", show: true, label: t('attendance.captainName') },
    { prop: "mobile", show: true, label: t('field.mobile') },
    { prop: "username", show: true, label: t('field.username') },
    { prop: "operation", show: true, label: t('table.operation'), width: 140 },
]);

const applyLabels = () => {
    searchData[0].label = t('field.keywords');
    searchData[0].placeholder = t('attendance.placeholderNameUserMobile');
    state.column = buildColumns();
    tableHeaderFn();
};

useAttendancePageI18n(applyLabels);

const state = reactive({
	loading: false,
    column: buildColumns(),
    tableHeader: [],
    tableData: [],
    offsetHeight: 0,
});

const showEfficiencySwitch = computed(() => hasRecords(selectedDay.value?.gotowork));
const showAttitudeSwitch = computed(() => hasRecords(selectedDay.value?.gooffwork));
const showConstructionFeedbackSwitch = computed(() => {
    return hasRecords(selectedDay.value?.zhongzhi) || hasRecords(selectedDay.value?.OT);
});

const hasRecords = (arr) => Array.isArray(arr) && arr.length > 0;

const hasEvaluated = (day) => hasRecords(day?.pingjia);

const renderCheckinLine = (arr, field) => {
    if (!hasRecords(arr)) return t('attendance.noRecord');
    return arr
        .map((item) => {
            if (field === "checkin_time") return item?.checkin_time || "-";
            const time = item?.checkin_time || "-";
            const info = item?.[field] ? ` · ${item[field]}` : "";
            return `${time}${info}`;
        })
        .join(" / ");
};

const parsePhotoField = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) {
        return value
            .map((item) => (typeof item === "string" ? item : item?.url || item?.image_url || ""))
            .filter(Boolean);
    }
    if (typeof value === "string") {
        const text = value.trim();
        if (!text) return [];
        if (text.startsWith("http")) return [text];
        try {
            const parsed = JSON.parse(text);
            return parsePhotoField(parsed);
        } catch {
            return [];
        }
    }
    return [];
};

const parseMobileUrlField = (value) => {
    if (!value || typeof value !== "string") return [];
    try {
        const arr = JSON.parse(value);
        if (!Array.isArray(arr)) return [];
        return arr
            .map((item) => (item?.url ? String(item.url) : ""))
            .filter(Boolean);
    } catch {
        return [];
    }
};

const signedPhotoMap = reactive({});

const collectCheckinPhotosRaw = (arr) => {
    if (!hasRecords(arr)) return [];
    const photos = arr.flatMap((item) => {
        return [
            ...parseMobileUrlField(item?.url),
            ...parsePhotoField(item?.signature_image_url),
            ...parsePhotoField(item?.photos),
            ...parsePhotoField(item?.photo),
            ...parsePhotoField(item?.img_url),
            ...parsePhotoField(item?.image_url),
        ];
    });
    return [...new Set(photos)];
};

const photoCacheKey = (arr) => collectCheckinPhotosRaw(arr).join("|");

const ensureSignedPhotos = async (arr) => {
    const key = photoCacheKey(arr);
    if (!key || signedPhotoMap[key]) return;
    const urls = collectCheckinPhotosRaw(arr);
    signedPhotoMap[key] = await signOssUrls(urls);
};

const collectCheckinPhotos = (arr) => {
    const key = photoCacheKey(arr);
    if (key) {
        ensureSignedPhotos(arr);
        if (signedPhotoMap[key]) {
            return signedPhotoMap[key];
        }
    }
    return collectCheckinPhotosRaw(arr);
};

const renderReviewSummary = (day) => {
    if (!hasEvaluated(day)) return t('attendance.none');
    const review = day.pingjia[0] || {};
    const tags = [];
    if (Number(review.working_efficiency) === 1) tags.push(t('attendance.efficiencyPass'));
    if (Number(review.working_attitude) === 1) tags.push(t('attendance.attitudePass'));
    if (Number(review.construction_feedback) === 1) tags.push(t('attendance.constructionPass'));
    if (Number(review.complaint_deduction) === 1) tags.push(t('attendance.hasComplaint'));
    return `${review.content || t('attendance.noContent')}${tags.length ? "（" + tags.join(" / ") + "）" : ""}`;
};

const offHeighths = () => {
    if (!myElementRef.value) return;
    state.offsetHeight = myElementRef.value?.offsetHeight - 90 - (searchBox.value?.offsetHeight || 0);
};

const tableHeaderFn = () => {
    state.tableHeader = state.column.filter((v) => v.show);
};

const pageChange = (currentPage) => {
    page.value = currentPage;
    getSearch(searchData);
};

const limitChange = (currentLimit) => {
    limit.value = currentLimit;
    getSearch(searchData);
};

const getSearch = (params) => {
    const rawParams = toRaw(params).map((i) => ({ ...i, value: i.value || "" }));
    const form = listToMap(rawParams, { value: "value", key: "field" });
    const requestData = {
        page: page.value,
        limit: limit.value,
        role_id: 2,
        employment: 1,
        ...form,
    };
    state.loading = true;
    captainListApi(JSON.stringify(requestData))
        .then((res) => {
            maxPage.value = res?.maxPage ?? 0;
            state.tableData = res?.data ?? [];
        })
        .finally(() => {
            state.loading = false;
        });
};

const openReviewDialog = (row) => {
    selectedCaptain.value = row;
    reviewDialogTitle.value = `${t('attendance.captainReview')}（${row?.real_name || "-"}）`;
    reviewTab.value = 1;
    dayList.value = [];
    reviewDialogVisible.value = true;
    fetchCheckinDays();
};

const fetchCheckinDays = () => {
    if (!selectedCaptain.value?.id) return;
    reviewLoading.value = true;
    const data = {
        page: 1,
        user_id: selectedCaptain.value.id,
        lx: reviewTab.value,
    };
    captainCheckinDaysApi(JSON.stringify(data))
        .then((res) => {
            dayList.value = res?.data ?? [];
        })
        .finally(() => {
            reviewLoading.value = false;
        });
};

const onChangeReviewTab = () => {
    fetchCheckinDays();
};

const resetEvaluateForm = () => {
    evaluateForm.content = "";
    evaluateForm.working_efficiency = false;
    evaluateForm.working_attitude = false;
    evaluateForm.construction_feedback = false;
    evaluateForm.complaint_deduction = false;
};

const openEvaluateDialog = (day) => {
    selectedDay.value = day;
    evaluateDate.value = day?.checkin_time || "";
    resetEvaluateForm();
    evaluateDialogVisible.value = true;
};

const submitEvaluate = () => {
    if (!isSuperAdmin.value) {
        ElMessage.warning(t('attendance.onlySuperAdminSubmit'));
        return;
    }
    if (!selectedCaptain.value?.id || !evaluateDate.value) {
        ElMessage.error(t('attendance.missingReviewTarget'));
        return;
    }
    if (!reviewerId) {
        ElMessage.error(t('attendance.cannotGetCurrentUser'));
        return;
    }
    submitting.value = true;
    const params = {
        reviewer_id: reviewerId,
        user_id: selectedCaptain.value.id,
        checkin_date: evaluateDate.value,
        content: evaluateForm.content,
        working_efficiency: evaluateForm.working_efficiency ? 1 : 0,
        working_attitude: evaluateForm.working_attitude ? 1 : 0,
        construction_feedback: evaluateForm.construction_feedback ? 1 : 0,
        complaint_deduction: evaluateForm.complaint_deduction ? 1 : 0,
    };
    saveCaptainReviewApi(JSON.stringify(params))
        .then((res) => {
            if (res?.code === 200) {
                ElMessage.success(t('attendance.reviewSubmitSuccess'));
                evaluateDialogVisible.value = false;
                fetchCheckinDays();
            } else {
                ElMessage.error(res?.msg || t('attendance.reviewSubmitFailed'));
            }
        })
        .catch(() => {
            ElMessage.error(t('attendance.reviewSubmitFailed'));
        })
        .finally(() => {
            submitting.value = false;
        });
};

onMounted(() => {
    applyLabels();
    nextTick(() => {
        offHeighths();
    });
    window.onresize = () => {
        nextTick(() => {
            offHeighths();
        });
    };
    tableHeaderFn();
    getSearch(searchData);
});

onUnmounted(() => {
    window.onresize = null;
});
</script>

<style lang="less" scoped>
.finance_box {
    flex: 1;
    overflow: auto;
    background-color: #fff;
    border-radius: 6px;
    padding: 10px;
    margin-right: 10px;
}

.table_box {
    width: 100%;
}

.opBtn {
    margin-right: 10px;
}

.role-alert {
    margin-bottom: 10px;
}

.dialog-toolbar {
    margin-bottom: 12px;
}

.popup-date-hint {
    text-align: center;
    color: #909399;
    font-size: 13px;
    margin-bottom: 12px;
}

.eval-switch-row {
    display: flex;
    align-items: center;
    gap: 8px;
}

.day-list-wrap {
    min-height: 320px;
    max-height: 560px;
    overflow-y: auto;
}

.day-card {
    border: 1px solid #ebeef5;
    border-radius: 8px;
    padding: 12px;
    margin-bottom: 10px;
}

.day-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;
}

.day-title {
    font-weight: 600;
    color: #303133;
}

.line-item {
    margin-bottom: 8px;
    line-height: 1.5;
}

.line-item .k {
    color: #606266;
}

.line-item .v {
    color: #303133;
}

.photo-row {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin: -2px 0 8px 52px;
}

.record-img {
    width: 120px;
    height: 90px;
    border-radius: 6px;
    object-fit: cover;
    border: 1px solid #ebeef5;
}

.card-actions {
    display: flex;
    justify-content: flex-end;
}
</style>
